/* Eloss — cart drawer
 *
 * Spec: §H.2, §N (dialog semantics, focus trap and restoration, `inert` for
 * background content, reduced motion), §P.5, §P.6.
 *
 * Mirrors the drawer pattern already proven in component-header.js rather than
 * inventing a second one: store the opener, inert + aria-hidden the background,
 * trap Tab, close on Escape and on the overlay, restore focus on close.
 *
 * TWO THINGS THE HEADER DRAWER DOES NOT HAVE TO HANDLE, and this one does:
 *
 *   1. Its contents are REPLACED on every cart mutation (§H.1). So the focus
 *      trap queries its focusable elements live on every Tab — a cached list
 *      would point at detached nodes the moment a line is removed. For the same
 *      reason the close button is reached by delegation, not by a direct
 *      binding to a node that will not survive.
 *
 *   2. Removing the last line deletes every control the visitor could be
 *      standing on, and the browser drops focus to <body> — the same failure
 *      the phase 3 variant picker had. After a swap, if focus has been lost,
 *      it is moved to the drawer heading, which is why that heading carries
 *      tabindex="-1".
 *
 * Opening is driven by the eloss:cart:added event (§P.6), not by a direct call
 * from the product form, so there is one contract and one path. A 422 never
 * emits that event, which is how §H.2's "errors do not open the drawer" holds.
 */

(() => {
  'use strict';

  const Eloss = window.Eloss;
  if (!Eloss) return;

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const focusable = (root) =>
    Array.from(root.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null || el === document.activeElement
    );

  Eloss.define(
    'eloss-cart-drawer',
    class ElossCartDrawer extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('[data-cart-dialog]');
        this.overlay = this.querySelector('[data-cart-overlay]');
        if (!this.dialog) return;

        /* Delegated: the close button lives inside the subtree that gets
           replaced on every mutation. */
        this.onClick = (event) => {
          if (event.target.closest('[data-cart-close]')) {
            event.preventDefault();
            this.close();
          }
        };
        this.addEventListener('click', this.onClick);

        this.onOverlay = () => this.close();
        this.overlay?.addEventListener('click', this.onOverlay);

        this.onKeydown = (event) => {
          if (!this.isOpen) return;
          if (event.key === 'Escape') {
            event.stopPropagation();
            this.close();
          } else if (event.key === 'Tab') {
            this.trapFocus(event);
          }
        };
        this.dialog.addEventListener('keydown', this.onKeydown);

        /* The header's cart link opens the drawer instead of navigating.
           Bound here, from the drawer, so component-header.js stays free of
           cart logic. Without JS the same link still navigates to /cart. */
        this.onHeaderClick = (event) => {
          const trigger = event.target.closest('.header__icon--cart');
          if (!trigger) return;
          event.preventDefault();
          this.open(trigger);
        };
        document.addEventListener('click', this.onHeaderClick);

        this.onCartAdded = () => this.open(document.activeElement);
        document.addEventListener(Eloss.EVENTS.cartAdded, this.onCartAdded);

        this.onCartUpdated = () => this.restoreFocusAfterSwap();
        document.addEventListener(Eloss.EVENTS.cartUpdated, this.onCartUpdated);
      }

      disconnectedCallback() {
        /* Everything attached outside this element must come off here, or it
           outlives the element across Theme Editor re-renders. */
        this.removeEventListener('click', this.onClick);
        this.overlay?.removeEventListener('click', this.onOverlay);
        this.dialog?.removeEventListener('keydown', this.onKeydown);
        document.removeEventListener('click', this.onHeaderClick);
        document.removeEventListener(Eloss.EVENTS.cartAdded, this.onCartAdded);
        document.removeEventListener(Eloss.EVENTS.cartUpdated, this.onCartUpdated);
        this.releaseBackground();
      }

      get isOpen() {
        return this.dialog && !this.dialog.hidden;
      }

      open(opener) {
        if (this.isOpen) return;
        this.returnFocusTo = opener instanceof HTMLElement ? opener : null;

        this.dialog.hidden = false;
        if (this.overlay) this.overlay.hidden = false;
        document.documentElement.style.overflow = 'hidden';

        /* Reveal, force a style/layout flush so the browser records the
           closed translate as the transition's start value, then open.
           Deliberately NOT requestAnimationFrame: rAF does not run while the
           tab is not compositing (a background tab, or a hidden preview pane),
           which would leave the drawer functionally open but parked off
           screen. Reading offsetWidth is synchronous and always happens. */
        void this.dialog.offsetWidth;
        this.dialog.classList.add('is-open');

        /* §N.1 — inert AND aria-hidden the background, with the focus trap
           below as the behavioural guarantee rather than relying on inert. */
        this.inerted = Array.from(document.body.children).filter(
          (el) => el !== this && !el.contains(this)
        );
        this.inerted.forEach((el) => {
          el.inert = true;
          el.setAttribute('aria-hidden', 'true');
        });

        focusable(this.dialog)[0]?.focus();
        this.loadRecommendations();
      }

      close() {
        if (!this.isOpen) return;

        /* Hidden immediately rather than after a transitionend: an exit
           animation that never fires its event would leave the drawer stuck
           open and the background inert. Correctness over a flourish. */
        this.dialog.classList.remove('is-open');
        this.dialog.hidden = true;
        if (this.overlay) this.overlay.hidden = true;
        document.documentElement.style.overflow = '';
        this.releaseBackground();

        /* document.body is rejected, not just null-checked: Safari leaves
           activeElement on <body> when a button is clicked, so restoring to it
           would drop focus to the top of the document. */
        const target =
          this.returnFocusTo &&
          this.returnFocusTo !== document.body &&
          document.contains(this.returnFocusTo)
            ? this.returnFocusTo
            : document.querySelector('.header__icon--cart');
        target?.focus();
        this.returnFocusTo = null;
      }

      releaseBackground() {
        this.inerted?.forEach((el) => {
          el.inert = false;
          el.removeAttribute('aria-hidden');
        });
        this.inerted = null;
      }

      trapFocus(event) {
        /* Queried live — the contents are replaced on every mutation. */
        const items = focusable(this.dialog);
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      /**
       * A swap can delete the node that had focus — removing the last line
       * deletes the whole list. Put focus somewhere sensible rather than
       * leaving the visitor on <body> at the top of the document.
       */
      restoreFocusAfterSwap() {
        if (!this.isOpen) return;
        const active = document.activeElement;
        if (active && active !== document.body && this.contains(active)) return;
        this.querySelector('[data-cart-dialog] h2')?.focus();
      }

      /**
       * §H.2 — recommendations are fetched when the drawer opens, never on page
       * load, and the section hides itself when the response is empty.
       */
      async loadRecommendations() {
        const host = this.querySelector('[data-cart-recommendations]');
        if (!host || host.dataset.loaded === 'true') return;
        const url = host.dataset.recommendationsUrl;
        if (!url) return;

        try {
          const response = await fetch(url);
          if (!response.ok) return;
          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const section = doc.querySelector('.cart-recommendations');
          if (section) host.appendChild(section);
          host.dataset.loaded = 'true';
        } catch (error) {
          /* Recommendations are additive. A failure here must never disturb a
             cart the visitor can already check out from. */
          console.warn('Eloss: cart recommendations unavailable', error);
        }
      }
    }
  );
})();
